import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);
const WishlistContext = createContext(null);

const CART_KEY = "ae_cart_v1";
const WISH_KEY = "ae_wishlist_v1";

function loadLS(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveLS(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

function itemKey(it) {
  return `${it.product_id}__${it.size || ""}__${it.color || ""}`;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => loadLS(CART_KEY, []));
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => saveLS(CART_KEY, items), [items]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const key = itemKey(item);
      const idx = prev.findIndex((p) => itemKey(p) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: Math.min(10, next[idx].quantity + item.quantity) };
        return next;
      }
      return [...prev, item];
    });
    setDrawerOpen(true);
  }, []);

  const updateQuantity = useCallback((key, qty) => {
    setItems((prev) => prev.map((it) => (itemKey(it) === key ? { ...it, quantity: Math.max(1, Math.min(10, qty)) } : it)));
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((it) => itemKey(it) !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((s, it) => s + it.quantity, 0), [items]);

  const value = {
    items, addItem, updateQuantity, removeItem, clear, count,
    drawerOpen, setDrawerOpen, itemKey,
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

export function WishlistProvider({ children }) {
  const [ids, setIds] = useState(() => loadLS(WISH_KEY, []));
  useEffect(() => saveLS(WISH_KEY, ids), [ids]);

  const toggle = useCallback((productId) => {
    setIds((prev) => (prev.includes(productId) ? prev.filter((x) => x !== productId) : [...prev, productId]));
  }, []);

  const has = useCallback((productId) => ids.includes(productId), [ids]);
  const clear = useCallback(() => setIds([]), []);
  return <WishlistContext.Provider value={{ ids, toggle, has, clear, count: ids.length }}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error("useWishlist must be used within WishlistProvider");
  return ctx;
}
